import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, getUserId } from "@/lib/api";
import { SectionHead } from "@/pages/Home";
import { ArrowRight, BookOpen, Lock, Sparkles, Compass, Feather } from "lucide-react";
import { AudioPlayer } from "@/components/Voice";
import { MyCarnets } from "@/components/MyCarnets";
import { MyAccess } from "@/components/MyAccess";
import { MyCedaLink } from "@/components/ceda/MyCedaLink";
import { MyGifts } from "@/components/MyGifts";

export default function MonEspace() {
  const userId = getUserId();
  const [books, setBooks] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/books"), api.get(`/progress/${userId}`)])
      .then(([b, p]) => {
        setBooks(b.data);
        setProgress(p.data);
      })
      .finally(() => setLoading(false));
  }, [userId]);

  const active = progress
    .map((p) => ({ ...p, book: books.find((b) => b.id === p.book_id) }))
    .filter((p) => p.book && (p.completed_days || []).length > 0)
    .sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-14 md:py-20">
      <SectionHead eyebrow="Votre espace personnel" title="Mon Tao des 4C" />

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="espace-quick-access">
        {[
          { key: "univers", icon: Compass, label: "Mes Univers", to: "/univers" },
          { key: "aventures", icon: BookOpen, label: "Mes Aventures", to: "/aventures" },
          { key: "jeux", icon: Sparkles, label: "Mes Jeux", to: "/jeu/ile" },
          { key: "carnets", icon: Feather, label: "Mes Carnets", to: "/boutique/carnets" },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <Link key={s.key} to={s.to} data-testid={`espace-nav-${s.key}`} className="group rounded-2xl bg-white border border-[#E8DCC4] p-5 text-center hover:border-[#C9A24B]/60 transition-colors">
              <span className="w-11 h-11 mx-auto rounded-full bg-[#C9A24B]/15 text-[#C9A24B] flex items-center justify-center"><Icon size={20} /></span>
              <p className="font-serif text-base text-[#1F2A24] mt-3">{s.label}</p>
            </Link>
          );
        })}
      </div>

      <MyAccess />

      <MyCarnets />

      <MyCedaLink />

      <MyGifts />

      <h3 className="font-serif text-2xl text-[#1F2A24] mt-12">Ma progression</h3>

      {loading ? (
        <p className="mt-10 font-sans text-[#1F2A24]/60">Chargement…</p>
      ) : active.length === 0 ? (
        <div data-testid="espace-empty" className="mt-10 rounded-2xl bg-white border border-[#E8DCC4] p-8 text-center">
          <BookOpen className="mx-auto text-[#C9A24B]" size={32} />
          <p className="font-serif text-xl text-[#1F2A24] mt-4">Votre chemin commence ici</p>
          <p className="font-sans text-sm text-[#1F2A24]/70 mt-2">
            Vous n'avez pas encore commencé de carnet. Choisissez-en un pour débuter.
          </p>
          <Link
            to="/carnets"
            data-testid="espace-discover"
            className="inline-flex items-center gap-2 rounded-full bg-[#385E3A] text-white px-6 py-3 font-ui text-sm mt-6 hover:bg-[#2c4a2e] transition-colors"
          >
            DÉCOUVRIR LES CARNETS <ArrowRight size={16} />
          </Link>
        </div>
      ) : (
        <div className="mt-10 space-y-5" data-testid="espace-progress-list">
          {active.map((p) => {
            const done = (p.completed_days || []).length;
            const pct = Math.round((done / 21) * 100);
            const lastDay = Math.max(0, ...(p.completed_days || [0]));
            return (
              <div
                key={p.book_id}
                data-testid={`espace-carnet-${p.book.number}`}
                className="rounded-2xl bg-white border border-[#E8DCC4] p-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span className="font-ui text-[11px] tracking-widest text-[#C9A24B]">
                      CARNET {String(p.book.number).padStart(2, "0")}
                    </span>
                    <h3 className="font-serif text-xl text-[#1F2A24]">{p.book.title}</h3>
                  </div>
                  <span className="font-serif text-2xl text-[#385E3A]">{done}/21</span>
                </div>

                <div className="h-2 rounded-full bg-[#E8DCC4] mt-4 overflow-hidden">
                  <div className="h-full bg-[#C9A24B]" style={{ width: `${pct}%` }} />
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 mt-4 font-sans text-sm text-[#1F2A24]/70">
                  <span>Jour actuel : {p.current_day} • Dernier terminé : {lastDay || "—"}</span>
                  <Link
                    to={`/carnet/${p.book_id}`}
                    data-testid={`espace-continue-${p.book.number}`}
                    className="inline-flex items-center gap-2 rounded-full bg-[#385E3A] text-white px-5 py-2.5 font-ui text-sm hover:bg-[#2c4a2e] transition-colors"
                  >
                    CONTINUER <ArrowRight size={16} />
                  </Link>
                </div>
                <RendezvousSummary userId={userId} bookId={p.book_id} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const RendezvousSummary = ({ userId, bookId }) => {
  const [meta, setMeta] = useState([]);
  useEffect(() => {
    api.get(`/recordings/${userId}/${bookId}`).then((r) => setMeta(r.data || [])).catch(() => {});
  }, [userId, bookId]);
  const d1 = meta.find((m) => m.slot === "day1");
  const d21 = meta.find((m) => m.slot === "day21");
  if (!d1 && !d21) return null;
  return (
    <div data-testid="espace-rendezvous" className="mt-4 rounded-xl border border-[#C9A24B]/30 bg-[#F8F5EE] p-4">
      <p className="font-ui text-[11px] tracking-[0.2em] text-[#C9A24B]">MON RENDEZ-VOUS VOCAL</p>
      <div className="mt-3 space-y-3">
        {d1 && (d1.locked ? (
          <p className="font-sans text-sm text-[#1F2A24]/60 inline-flex items-center gap-2">
            <Lock size={14} /> Message du Jour 1 conservé — déverrouillé au Jour 21
          </p>
        ) : (
          <div>
            <p className="font-sans text-xs text-[#1F2A24]/60 mb-1">Jour 1 — Moi du futur → moi d'aujourd'hui</p>
            <AudioPlayer userId={userId} bookId={bookId} slot="day1" label="ÉCOUTER LE JOUR 1" />
          </div>
        ))}
        {d21 && (
          <div>
            <p className="font-sans text-xs text-[#1F2A24]/60 mb-1">Jour 21 — Ma réponse</p>
            <AudioPlayer userId={userId} bookId={bookId} slot="day21" label="ÉCOUTER LE JOUR 21" />
          </div>
        )}
      </div>
    </div>
  );
};
